import { useDispatch, useSelector } from "react-redux";
import {
  getUserData,
  getUserDataTimeZone,
  setExamHistory,
} from "../../store/login.slice";
import { useEffect, useState } from "react";
import { formatDateTime, limitDigits } from "../../helpers/formatters";
import {
  useGardeTestMutation,
  useGettestsQuery,
} from "../../api/testSlice.ts/testSlice";
import useTokenSpoilCheck from "../../hooks/tokenspoilcheck";
import { toast } from "react-toastify";
import { CheckSquare } from "lucide-react";
import styles from "./testLists.module.scss";
import { setReviewedExam } from "../../store/test.slice";
import { useNavigate } from "react-router-dom";

function CompleteTests() {
  //
  //Selectors
  //
  const userData = useSelector(getUserData);
  const timeZone = useSelector(getUserDataTimeZone);
  //
  //local state
  //
  const [expanded, setExpanded] = useState([]);
  const [errorStatusCode, setErrorStatusCode] = useState(undefined);
  const [sortingMode, setSortingMode] = useState({
    start: "",
    last: "DESC",
    finish: "",
    grade: "",
    questions: "",
  });
  const [selectedExam, setSelectedExam] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState("");

  //
  //Hooks
  //
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useTokenSpoilCheck(errorStatusCode);

  //
  //Queries
  //
  const { data: tests, refetch } = useGettestsQuery({});

  const [gardeTest] = useGardeTestMutation();
  //
  //UseEffect hooks
  //
  useEffect(() => {
    refetch();
  }, [refetch]);
  useEffect(() => {
    if (tests?.CompletedTests && tests?.IncompleteTests) {
      const CompletedTests = tests?.CompletedTests;
      const IncompleteTests = tests.IncompleteTests;
      dispatch(setExamHistory({ CompletedTests, IncompleteTests }));
    }
  }, [tests]);

  // Pre-select the most recently used test
  useEffect(() => {
    if (tests?.CompletedTests?.length > 0) {
      const mostRecentTest = tests.CompletedTests.reduce((latest, test) =>
        new Date(test.LastUsed) > new Date(latest.LastUsed) ? test : latest,
      );
      setSelectedExam(mostRecentTest);
    }
  }, [tests]);

  //
  //Copy protection
  //
  useEffect(() => {
    // Disable right-click context menu
    const disableContextMenu = (event) => event.preventDefault();
    document.addEventListener("contextmenu", disableContextMenu);

    // Disable copy and cut keyboard shortcuts
    const disableCopyCut = (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "c" || event.key === "x")
      ) {
        event.preventDefault();
      }
    };
    document.addEventListener("keydown", disableCopyCut);

    // Disable the copy event
    const disableCopyEvent = (event) => {
      event.preventDefault();
    };
    document.addEventListener("copy", disableCopyEvent);

    // Cleanup event listeners on unmount
    return () => {
      document.removeEventListener("contextmenu", disableContextMenu);
      document.removeEventListener("keydown", disableCopyCut);
      document.removeEventListener("copy", disableCopyEvent);
    };
  }, []);

  //
  //Util funcs
  //
  const fetchTestReview = async (guid, prevData) => {
    let result;
    try {
      result = await gardeTest({ testData: { guid } }).unwrap();
      dispatch(
        setReviewedExam({
          examToReview: { test: result, guid: guid, prevData },
        }),
      );
      // The review is its own full page, laid out like the test page
      navigate("/reviewTest");
    } catch (error) {
      toast(error.data.message);
      setErrorStatusCode(error?.status);
      console.log(error);
    }
  };

  let Data = [];
  if (userData.CompletedTests) {
    Data = [...userData.CompletedTests];
  }

  const sortedData = Data.sort((a, b) => {
    if (sortingMode.start) {
      const startA = new Date(a.TimeTestStarted).getTime();
      const startB = new Date(b.TimeTestStarted).getTime();
      return sortingMode.start === "ASC" ? startA - startB : startB - startA;
    }

    if (sortingMode.finish) {
      const finishA = new Date(a.TimeTestFinished).getTime();
      const finishB = new Date(b.TimeTestFinished).getTime();
      return sortingMode.finish === "ASC"
        ? finishA - finishB
        : finishB - finishA;
    }

    if (sortingMode.last) {
      const lastA = new Date(a.LastUsed).getTime();
      const lastB = new Date(b.LastUsed).getTime();
      return sortingMode.last === "ASC" ? lastA - lastB : lastB - lastA;
    }

    if (sortingMode.grade) {
      const gradeA = a?.GradeForTest ?? -Infinity;
      const gradeB = b?.GradeForTest ?? -Infinity;
      return sortingMode.grade === "ASC" ? gradeA - gradeB : gradeB - gradeA;
    }

    if (sortingMode.questions) {
      const questionsA = a?.questions_for_test?.length ?? 0;
      const questionsB = b?.questions_for_test?.length ?? 0;
      return sortingMode.questions === "ASC"
        ? questionsA - questionsB
        : questionsB - questionsA;
    }
    return 0; // Default case when no sorting mode is applied
  });

  const visibleData = sortedData.filter((test) => {
    const term = searchTerm.toLowerCase();
    return (
      (test?.TestName || "").toLowerCase().includes(term) ||
      (test?.guid || "").toLowerCase().includes(term)
    );
  });

  const clearedSorting = {
    start: "",
    last: "",
    finish: "",
    grade: "",
    questions: "",
  };

  const cycleSort = (key) => {
    const mode = sortingMode[key];
    const next = mode === "" ? "ASC" : mode === "ASC" ? "DESC" : "";
    setSortingMode({ ...clearedSorting, [key]: next });
  };

  const sortIndicator = (mode) =>
    mode === "ASC" ? " ▲" : mode === "DESC" ? " ▼" : "";

  const getGradeTier = (grade) =>
    grade == undefined
      ? ""
      : Number(grade) >= 70
        ? styles.gradeHigh
        : Number(grade) >= 50
          ? styles.gradeMid
          : styles.gradeLow;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.titleContainer}>
          <CheckSquare />
          <h1 className={styles.titleText}>Complete Tests</h1>
        </div>

        <div className={styles.filterContainer}>
          <p className={styles.countText}>
            {visibleData.length} test{visibleData.length == 1 ? "" : "s"}{" "}
            completed
          </p>
          <div className={styles.controls}>
            <input
              className={styles.searchInput}
              placeholder="Search by test name or ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className={styles.filters}>
              <button
                className={styles.primaryButton}
                onClick={() => cycleSort("start")}
              >
                Sort By Start Date {sortingMode.start}
              </button>
              <button
                className={styles.primaryButton}
                onClick={() => cycleSort("last")}
              >
                Sort By Last Used {sortingMode.last}
              </button>
              <button
                className={styles.primaryButton}
                onClick={() => cycleSort("finish")}
              >
                Sort By Finished {sortingMode.finish}
              </button>
              <button
                className={styles.primaryButton}
                onClick={() => cycleSort("questions")}
              >
                Sort By Questions {sortingMode.questions}
              </button>
              <button
                className={styles.primaryButton}
                onClick={() => cycleSort("grade")}
              >
                Sort By Grade {sortingMode.grade}
              </button>
            </div>
          </div>
        </div>

        {visibleData.length > 0 && (
          <div className={styles.tableWrapper}>
            <div className={styles.tableScroll}>
              <table className={`${styles.table} ${styles.completeTable}`}>
                <thead>
                  <tr>
                    <th>Test</th>
                    <th
                      className={styles.sortableHeader}
                      onClick={() => cycleSort("start")}
                    >
                      Started{sortIndicator(sortingMode.start)}
                    </th>
                    <th
                      className={styles.sortableHeader}
                      onClick={() => cycleSort("finish")}
                    >
                      Finished{sortIndicator(sortingMode.finish)}
                    </th>
                    <th
                      className={styles.sortableHeader}
                      onClick={() => cycleSort("questions")}
                    >
                      Questions{sortIndicator(sortingMode.questions)}
                    </th>
                    <th
                      className={styles.sortableHeader}
                      onClick={() => cycleSort("grade")}
                    >
                      Grade{sortIndicator(sortingMode.grade)}
                    </th>
                    <th
                      className={styles.sortableHeader}
                      onClick={() => cycleSort("last")}
                    >
                      Last used{sortIndicator(sortingMode.last)}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleData.map((test) => {
                    const started = new Date(test.TimeTestStarted);
                    const lastUsed = new Date(test.LastUsed);
                    const finished = new Date(test.TimeTestFinished);
                    const grade =
                      test?.GradeForTest != undefined
                        ? limitDigits(test?.GradeForTest)
                        : undefined;
                    const gradeTier = getGradeTier(grade);
                    return (
                      <tr
                        key={test.TimeTestStarted}
                        className={
                          selectedExam?.guid == test.guid
                            ? styles.selectedOption
                            : ""
                        }
                        onClick={() => setSelectedExam(test)}
                      >
                        {test.TestName ? (
                          <td title={test.guid}>{test.TestName}</td>
                        ) : (
                          <td className={styles.testIdCell}>
                            <button
                              className={styles.secondaryButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpanded((prev) =>
                                  prev.includes(test.guid)
                                    ? prev.filter((id) => id !== test.guid)
                                    : [...prev, test.guid],
                                );
                              }}
                            >
                              {expanded.includes(test.guid)
                                ? test?.guid || "Unknown"
                                : test?.guid?.slice(0, 7) || "Unknown"}
                            </button>
                          </td>
                        )}
                        <td>{formatDateTime(timeZone?.value, started)}</td>
                        <td>
                          {test?.TimeTestFinished
                            ? formatDateTime(timeZone?.value, finished)
                            : "Unknown"}
                        </td>
                        <td>{test?.questions_for_test?.length ?? "Unknown"}</td>
                        <td>
                          {grade != undefined ? (
                            <div className={styles.gradeBadgeContainer}>
                              <span
                                className={`${styles.gradeBadge} ${gradeTier}`}
                              >
                                {grade}%
                              </span>
                              <div className={styles.gradeBarTrack}>
                                <div
                                  className={`${styles.gradeBarFill} ${gradeTier}`}
                                  style={{ width: `${grade}%` }}
                                />
                              </div>
                            </div>
                          ) : (
                            "Unknown"
                          )}
                        </td>
                        <td>
                          {test?.LastUsed
                            ? formatDateTime(timeZone?.value, lastUsed)
                            : "Unknown"}
                        </td>
                        <td className={styles.tableActionCell}>
                          <button
                            className={styles.reviewRowButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedExam(test);
                              fetchTestReview(test.guid, test);
                            }}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {sortedData.length == 0 && (
          <div className={styles.warning}>
            Your complete tests will appear here once you complete one or more
            tests
          </div>
        )}
        {sortedData.length > 0 && visibleData.length == 0 && (
          <div className={styles.warning}>No tests match your search.</div>
        )}
      </div>
      <div className={styles.footerContainer}>
        <button
          className={styles.primaryButton}
          onClick={() => {
            if (selectedExam == undefined) {
              toast("Please select a test");
              return;
            }
            fetchTestReview(selectedExam.guid, selectedExam);
          }}
        >
          <span>Review Test </span>
        </button>
      </div>
    </div>
  );
}

export default CompleteTests;
